import { useMemo } from 'react';
import {
  Alert,
  AlertTitle,
  Button,
  Flex,
  Separator,
  Skeleton,
} from '@kognitos/lattice';
import { useRetrieveWorkspaceBooks } from '@/generated/hooks/bdk/v2/services/library';
import { V1InvocationSource } from '@/generated/hooks/jeeves/models';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { FilterBuilder } from '@/shared/utils';
import { areAllRequiredInputsProvided } from '@/modules/automation-inputs/utils/inputValidation';
import { ExistingTriggersList } from './ExistingTriggersList';
import { WebhookTrigger } from './WebhookTrigger';
import { EmailTrigger } from './EmailTrigger';
import { ScheduleTrigger } from './ScheduleTrigger';
import { EMAIL_BOOK_NAME } from '../EmailTriggerDialog/constants';
import { BOOK_CATALOG_STALE_TIME } from '@/modules/integrations/constants';
import type { ITriggerTypeSelectorProps } from './types';
import { useTranslation } from 'react-i18next';
import { useAutomationTriggers } from '../../hooks/useAutomationTriggers';
import { PollTrigger } from './PollTrigger';

/**
 * TriggerTypeSelector Component
 *
 * Displays the available trigger type options for an automation.
 * Shows existing triggers for the automation first, followed by options
 * to add new triggers.
 *
 * Trigger options are conditionally shown based on available books:
 * - EmailTrigger is shown if the email book is available
 * - WebhookTrigger is shown if at least one book with triggers (excluding email) is available
 *
 * Layout:
 * [Existing Triggers] [Manual] | [Add New Trigger Options]
 *
 * @param props - Component props
 * @returns JSX element containing trigger type selection UI
 */
export function TriggerTypeSelector({
  inputSpecs,
  automationId,
  automationName,
  isReadOnly = false,
  isDraft = true,
  invocationDetails,
  defaultInputKeys,
}: ITriggerTypeSelectorProps) {
  const { t } = useTranslation('automation-inputs');
  const { t: tCommon } = useTranslation('common');

  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  // Derive selected trigger from invocationDetails (only in RunPage context)
  const { selectedTriggerId, isManualSelected, isScheduleSelected } =
    useMemo(() => {
      // If no invocationDetails provided (draft editing mode), use defaults
      if (!invocationDetails) {
        return {
          selectedTriggerId: undefined,
          isManualSelected: undefined,
          isScheduleSelected: false,
        };
      }

      const { invocation_source, trigger_id } = invocationDetails;

      // Manual invocation - select manual trigger
      if (invocation_source === V1InvocationSource.INVOCATION_SOURCE_MANUAL) {
        return {
          selectedTriggerId: undefined,
          isManualSelected: true,
          isScheduleSelected: false,
        };
      }

      // Trigger invocation - select the specific trigger
      if (
        invocation_source === V1InvocationSource.INVOCATION_SOURCE_TRIGGER &&
        trigger_id
      ) {
        return {
          selectedTriggerId: trigger_id,
          isManualSelected: false,
          isScheduleSelected: false,
        };
      }

      // Schedule invocation - select the schedule trigger
      if (invocation_source === V1InvocationSource.INVOCATION_SOURCE_SCHEDULE) {
        return {
          selectedTriggerId: undefined,
          isManualSelected: false,
          isScheduleSelected: true,
        };
      }

      // Default to manual for other cases (PAT, test, unspecified)
      return {
        selectedTriggerId: undefined,
        isManualSelected: true,
        isScheduleSelected: false,
      };
    }, [invocationDetails]);
  // Fetch all integrations that have triggers
  const {
    data: integrationsData,
    isLoading: isLoadingIntegrations,
    isError: isErrorIntegrations,
    refetch: refetchIntegrations,
    isRefetching: isRefetchingIntegrations,
  } = useRetrieveWorkspaceBooks(
    organizationId,
    workspaceId,
    {
      page_size: 100,
      filter: FilterBuilder.create().equals('has_triggers', true).build(),
    },
    {
      query: {
        enabled: !!organizationId && !!workspaceId,
        staleTime: BOOK_CATALOG_STALE_TIME,
      },
    },
  );

  // Fetch all integrations that have polls
  const { data: pollIntegrationsData, isLoading: isLoadingPollIntegrations } =
    useRetrieveWorkspaceBooks(
      organizationId,
      workspaceId,
      {
        page_size: 100,
        filter: FilterBuilder.create().equals('has_polls', true).build(),
      },
      {
        query: {
          enabled: !!organizationId && !!workspaceId,
          staleTime: BOOK_CATALOG_STALE_TIME,
        },
      },
    );

  // Check if webhook trigger (any non-email book with triggers) is available
  const hasWebhookTrigger = useMemo(() => {
    const books = integrationsData?.data?.books ?? [];
    return books.some(
      book => book.name?.toLowerCase() !== EMAIL_BOOK_NAME.toLowerCase(),
    );
  }, [integrationsData]);

  // Check if poll trigger is available (any book with polls)
  const hasPollTrigger = useMemo(() => {
    const books = pollIntegrationsData?.data?.books ?? [];
    return books.length > 0;
  }, [pollIntegrationsData]);

  // Email trigger is always available, so new trigger options are shown
  // whenever the selector is editable.
  const showNewTriggerOptions = !isReadOnly;

  // Check if inputSpecs has any items
  const hasInputSpecs = useMemo(
    () => inputSpecs && inputSpecs.length > 0,
    [inputSpecs],
  );

  // Fetch and match triggers from Grimoire and BDK
  const {
    matchedTriggers: automationTriggers,
    hasSchedule: hasScheduleTrigger,
    isScheduleEnabled,
    scheduleSummary,
    isLoading: isLoadingAutomationTriggers,
  } = useAutomationTriggers({ automationId, isDraft });

  // Determine if schedule should be disabled based on required inputs
  const shouldDisableSchedule = useMemo(
    () => !areAllRequiredInputsProvided(inputSpecs, defaultInputKeys ?? []),
    [inputSpecs, defaultInputKeys],
  );

  const renderTriggerOptions = () => {
    // The run view (read-only) only surfaces the trigger that initiated the run,
    // never the "add new trigger" options — so skip the integration fetch states
    // entirely (no loading skeletons or error/retry alerts flashing in).
    if (isReadOnly) {
      return null;
    }

    if (isErrorIntegrations) {
      return (
        <Alert variant="destructive">
          <AlertTitle>
            <Flex gap={2} align="center">
              {t('triggerTypes.error.retrieveTriggers')}
              <Button
                size="sm"
                variant="secondary"
                onClick={() => refetchIntegrations()}
                isLoading={isRefetchingIntegrations}
              >
                {tCommon('common.action.retry')}
              </Button>
            </Flex>
          </AlertTitle>
        </Alert>
      );
    }

    if (isLoadingIntegrations || isLoadingPollIntegrations) {
      return (
        <Flex align="center" justify="center" gap={3}>
          <Skeleton className="h-40 w-35" />
          <Skeleton className="h-40 w-35" />
          <Skeleton className="h-40 w-35" />
        </Flex>
      );
    }

    if (showNewTriggerOptions) {
      return (
        <>
          {/* Add new trigger options - conditionally shown based on available books */}
          {hasWebhookTrigger && (
            <WebhookTrigger
              inputSpecs={inputSpecs}
              automationId={automationId}
              isDraft={isDraft}
            />
          )}
          <EmailTrigger
            inputSpecs={inputSpecs}
            automationId={automationId}
            automationName={automationName}
            isDraft={isDraft}
          />
          {hasPollTrigger && (
            <PollTrigger
              inputSpecs={inputSpecs}
              automationId={automationId}
              isDraft={isDraft}
            />
          )}
        </>
      );
    }

    return isReadOnly ? null : (
      <Alert variant="default">
        <AlertTitle>{t('triggerTypes.noTriggersAvailable')}</AlertTitle>
      </Alert>
    );
  };

  return (
    <Flex
      data-tour-id="trigger-type-selector"
      direction="row"
      align="stretch"
      gap={3}
      className="overflow-x-auto p-2 -mx-2 mb-5"
    >
      {/* Existing triggers and Manual trigger */}
      <ExistingTriggersList
        automationTriggers={automationTriggers}
        isLoadingAutomationTriggers={isLoadingAutomationTriggers}
        automationId={automationId}
        inputSpecs={inputSpecs}
        isReadOnly={isReadOnly}
        isDraft={isDraft}
        selectedTriggerId={selectedTriggerId}
        isManualSelected={isManualSelected}
        isScheduleSelected={isScheduleSelected}
        hasSchedule={hasScheduleTrigger}
        isScheduleEnabled={isScheduleEnabled}
        scheduleSummary={scheduleSummary}
      />

      {/* Separator between existing/manual and add new trigger options - only show when there are inputSpecs */}
      {(hasInputSpecs || automationTriggers.length > 0) && !isReadOnly ? (
        <Flex align="center" justify="center">
          <Separator orientation="vertical" className="h-10" />
        </Flex>
      ) : null}
      {renderTriggerOptions()}

      {/* Show "Add Schedule" button if schedule trigger is enabled and no schedule
          exists. Hidden in read-only (run view), which only surfaces the trigger
          that actually initiated the run. */}
      {!hasScheduleTrigger && !isDraft && !isReadOnly && (
        <ScheduleTrigger
          automationId={automationId}
          disabled={shouldDisableSchedule}
        />
      )}
    </Flex>
  );
}
